import React from 'react';

import './src/sass/_base.scss';
import { wrapWithLaunchDarkly } from './src/helper/launchdarkly/launchdarkly-provider';
import { resetFlagUsage } from './src/helper/launchdarkly/feature-flag-usage-store';
import { wrapPageElementWithGtmNoScript } from './src/components/google-tag-manager-noscript';
import { initDataLayerDiagnostics } from './src/helper/data-layer-diagnostics-store';
import DiagnosticsPanel from './src/components/diagnostics-panel';
import { trackAnalyticsEvent } from './src/hooks/useAnalytics';

export const wrapRootElement = wrapWithLaunchDarkly;

export const wrapPageElement = ({ element, props }) => {
   const themeClass = props.location.pathname.startsWith('/showcase')
      ? 'page-theme--showcase'
      : 'page-theme--home';

   return (
      <div className={themeClass}>
         {wrapPageElementWithGtmNoScript({ element, props })}
         <DiagnosticsPanel />
      </div>
   );
};

export const onClientEntry = () => {
   initDataLayerDiagnostics();
};

// Enhanced smooth scrolling for anchor links
export const onRouteUpdate = ({ location }) => {
   // Gatsby is a long-lived SPA after initial load, so flag usage needs an
   // explicit reset when the user navigates to a new page.
   resetFlagUsage();

   document.body.classList.remove('overlay__active');

   // Handle anchor links with smooth scrolling
   if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
         setTimeout(() => {
            element.scrollIntoView({
               behavior: 'smooth',
               block: 'start',
               inline: 'nearest',
            });
         }, 100); // Small delay to ensure page is rendered
      }
   }
};

// Add smooth scrolling to all anchor links on client-side
export const onInitialClientRender = () => {
   const trackLinkClick = (e) => {
      const link = e.target.closest('a[href]');
      if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const href = link.getAttribute('href') ?? '';
      const isEmail = href.startsWith('mailto:');
      const itemId = link.closest('[data-analytics-item-id]')?.dataset.analyticsItemId;
      const section = link.closest('[data-analytics-section]')?.dataset.analyticsSection;
      const companyEl = link.closest('[data-analytics-company-id]');
      const roleEl = link.closest('[data-analytics-role-id]');
      const context = link.closest('nav')
         ? 'navigation'
         : link.closest('footer')
           ? 'footer'
           : link.closest('.quicklook-popup')
             ? 'quick_view'
             : link.closest('.product-single')
               ? 'product_detail'
               : 'content';
      const params = {
         link_context: context,
         link_text: link.textContent?.replace(/\s+/g, ' ').trim().slice(0, 100) ?? '',
         link_type: isEmail ? 'email' : link.hostname === window.location.hostname ? 'internal' : 'external',
         link_url: isEmail ? 'mailto:' : link.href,
         ...(itemId && { item_id: itemId }),
         ...(section && { page_section: section }),
         ...(companyEl && { company_id: companyEl.dataset.analyticsCompanyId }),
         ...(companyEl?.dataset.analyticsCompanyName && {
            company_name: companyEl.dataset.analyticsCompanyName,
         }),
         ...(roleEl && { role_id: roleEl.dataset.analyticsRoleId }),
         ...(roleEl?.dataset.analyticsRoleName && { role_name: roleEl.dataset.analyticsRoleName }),
      };

      trackAnalyticsEvent('link_click', params);
      if (context === 'navigation') trackAnalyticsEvent('navigation_click', params);
   };

   document.addEventListener('click', trackLinkClick);

   // Enhanced smooth scrolling with easing for anchor links
   const addSmoothScrolling = () => {
      document.addEventListener('click', (e) => {
         const link = e.target.closest('a[href*="#"]');
         if (!link) return;

         const href = link.getAttribute('href');
         const isCurrentPageAnchor =
            href.startsWith('#') || href.includes(window.location.pathname + '#');

         if (isCurrentPageAnchor) {
            e.preventDefault();
            const targetId = href.split('#')[1];
            const targetElement = document.getElementById(targetId);

            if (targetElement) {
               // Use native smooth scrolling for better performance
               targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                  inline: 'nearest',
               });

               // Update URL hash after a short delay
               setTimeout(() => {
                  window.history.replaceState(null, null, href);
               }, 100);
            }
         }
      });
   };

   // Add the smooth scrolling once DOM is ready
   if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', addSmoothScrolling);
   } else {
      addSmoothScrolling();
   }
};
